// CarrinhoContext.js

import React, { createContext, useState, useEffect } from "react";
import { descartarChave } from "../../utils/idempotencia";

const CarrinhoContext = createContext();

// ===== A trava do `formato` =====
//
// O item do carrinho tem um campo `formato` que significa QUAL VERSÃO está
// sendo comprada: "ebook" ou "fisico". O registro do livro (`ebooks.formato`)
// tem um campo com o MESMO NOME que significa outra coisa: o tamanho do
// impresso ("16x23 (160x230)", "Pocket (105x148)").
//
// Em 20/08/2026 essa colisão derrubou todas as compras feitas por card: os
// componentes desestruturavam o livro (`const { ..., formato } = livro`) e
// mandavam o tamanho físico para o carrinho. O backend só aceita
// "ebook"/"fisico", devolvia 422, e a venda morria antes de chegar ao gateway.
//
// Corrigir os componentes um a um apaga o incêndio, mas não impede a
// reincidência — basta alguém desestruturar um livro de novo. Por isso a
// defesa mora AQUI, no único caminho por onde um item entra no carrinho ou na
// lista de desejos. Não há como um valor sujo passar, venha de onde vier.
//
// `ebook` é o padrão porque todo card vende o digital (usa `livro.preco`; o
// impresso é `preco_impresso` e só existe no PrecoCard, que escolhe o formato
// explicitamente).
const FORMATOS_VALIDOS = ["ebook", "fisico"];

const normalizarFormato = (formato) =>
  FORMATOS_VALIDOS.includes(formato) ? formato : "ebook";

// Saneia um item avulso. Preserva o resto do objeto: só o `formato` é suspeito.
const sanearItem = (item) =>
  item && typeof item === "object"
    ? { ...item, formato: normalizarFormato(item.formato) }
    : item;

// Saneia o que veio do localStorage. Carrinho salvo ANTES da correção continua
// sujo no navegador do cliente; sem isto, quem já tinha um item envenenado
// seguiria sem conseguir comprar mesmo depois do deploy.
const sanearLista = (lista) => (Array.isArray(lista) ? lista.map(sanearItem) : []);

// Lê uma das listas do localStorage já saneada. Lista quebrada vira lista
// vazia: o carrinho do cliente pode estar corrompido por qualquer motivo, e uma
// exceção aqui derrubaria o provider inteiro — ou seja, o site.
const lerLista = (chave) => {
  try {
    return sanearLista(JSON.parse(localStorage.getItem(chave) || "[]"));
  } catch {
    return [];
  }
};

const CarrinhoProvider = ({ children }) => {
  const [carrinho, setCarrinho] = useState([]);
  const [salvos, setSalvos] = useState([]); // "Salvos para mais tarde" (no carrinho)
  const [listaDesejos, setListaDesejos] = useState([]); // "Minha Lista" (perfil)

  useEffect(() => {
    // Cada lista é saneada na leitura E regravada quando muda algo. Regravar
    // é o que tira o valor sujo do navegador de vez: sem isso ele voltaria a
    // cada recarga, e o carrinho ficaria certo em memória e errado no disco.
    const carrinhoLS = localStorage.getItem("carrinho");
    if (carrinhoLS) {
      try {
        const bruto = JSON.parse(carrinhoLS);
        const limpo = sanearLista(bruto);
        setCarrinho(limpo);
        if (JSON.stringify(bruto) !== JSON.stringify(limpo)) {
          localStorage.setItem("carrinho", JSON.stringify(limpo));
        }
      } catch {}
    }
    const salvosLS = localStorage.getItem("carrinho_salvos");
    if (salvosLS) {
      try {
        const bruto = JSON.parse(salvosLS);
        const limpo = sanearLista(bruto);
        setSalvos(limpo);
        if (JSON.stringify(bruto) !== JSON.stringify(limpo)) {
          localStorage.setItem("carrinho_salvos", JSON.stringify(limpo));
        }
      } catch {}
    }
    const listaLS = localStorage.getItem("lista_desejos");
    if (listaLS) {
      try {
        const bruto = JSON.parse(listaLS);
        const limpo = sanearLista(bruto);
        setListaDesejos(limpo);
        if (JSON.stringify(bruto) !== JSON.stringify(limpo)) {
          localStorage.setItem("lista_desejos", JSON.stringify(limpo));
        }
      } catch {}
    }
  }, []);

  // ===== Outra aba mexendo nas listas =====
  //
  // O evento `storage` só chega às abas DIFERENTES da que gravou, então não há
  // laço: quem escreveu não se escuta. Sem ele, duas abas da loja divergem em
  // silêncio — a do checkout segue com o carrinho antigo em memória e manda ao
  // servidor um pedido que o cliente já não vê na outra aba.
  //
  // Para o pedido duplicado, o que interessa é a linha do `descartarChave`: a
  // chave de idempotência do checkout vale para UM carrinho. Se o carrinho
  // mudou em outra aba, a chave guardada aqui descreve algo que não existe
  // mais, e reusá-la faria o servidor devolver o pedido errado.
  useEffect(() => {
    const aoMudarStorage = (e) => {
      // `key === null` é `localStorage.clear()`: recarrega as três listas.
      const chaves = ["carrinho", "carrinho_salvos", "lista_desejos"];
      if (e.key !== null && !chaves.includes(e.key)) return;
      if (e.key === null || e.key === "carrinho") {
        setCarrinho(lerLista("carrinho"));
        descartarChave();
      }
      if (e.key === null || e.key === "carrinho_salvos") setSalvos(lerLista("carrinho_salvos"));
      if (e.key === null || e.key === "lista_desejos") setListaDesejos(lerLista("lista_desejos"));
    };
    window.addEventListener("storage", aoMudarStorage);
    return () => window.removeEventListener("storage", aoMudarStorage);
  }, []);

  // Única porta de gravação do carrinho. Além de tirar a repetição do par
  // setCarrinho + localStorage, é aqui que a chave de idempotência do checkout
  // morre: carrinho diferente é compra diferente, e manter a chave faria o
  // servidor responder a nova compra com o pedido anterior.
  //
  // Por que não um `useEffect` observando `carrinho`: ele dispararia também na
  // carga inicial, quando o estado sai de `[]` para a lista lida do
  // localStorage. Isso apagaria a chave a cada F5 — justamente o momento em que
  // ela precisa sobreviver, porque recarregar é o que o cliente faz quando a
  // página parece travada.
  const persistCarrinho = (lista) => {
    setCarrinho(lista);
    localStorage.setItem("carrinho", JSON.stringify(lista));
    descartarChave();
  };

  // ===== Minha Lista (wishlist) =====
  const persistLista = (lista) => {
    setListaDesejos(lista);
    localStorage.setItem("lista_desejos", JSON.stringify(lista));
  };
  const estaNaLista = (id, formato) => listaDesejos.some((i) => i.id === id && (formato == null || i.formato === formato));
  const adicionarDesejo = (item) => {
    const limpo = sanearItem(item);
    if (listaDesejos.some((i) => i.id === limpo.id && i.formato === limpo.formato)) return;
    persistLista([...listaDesejos, limpo]);
  };
  const removerDesejo = (id, formato) => {
    persistLista(listaDesejos.filter((i) => !(i.id === id && (formato == null || i.formato === formato))));
  };
  const toggleDesejo = (item) => {
    const limpo = sanearItem(item);
    estaNaLista(limpo.id, limpo.formato)
      ? removerDesejo(limpo.id, limpo.formato)
      : adicionarDesejo(limpo);
  };

  const persistSalvos = (lista) => {
    setSalvos(lista);
    localStorage.setItem("carrinho_salvos", JSON.stringify(lista));
  };

  // Move um item do carrinho para "Salvos para mais tarde"
  const salvarParaDepois = (id, formato) => {
    const item = carrinho.find((i) => i.id === id && i.formato === formato);
    if (!item) return;
    const novoCarrinho = carrinho.filter((i) => !(i.id === id && i.formato === formato));
    persistCarrinho(novoCarrinho);
    if (!salvos.some((s) => s.id === id && s.formato === formato)) {
      persistSalvos([...salvos, item]);
    }
  };

  // Move um item salvo de volta para o carrinho
  const moverParaCarrinho = (id, formato) => {
    const item = salvos.find((s) => s.id === id && s.formato === formato);
    if (!item) return;
    persistSalvos(salvos.filter((s) => !(s.id === id && s.formato === formato)));
    setCarrinho((prev) => {
      const idx = prev.findIndex((i) => i.id === id && i.formato === formato);
      let novo;
      if (idx === -1) novo = [...prev, { ...item, quantidade: item.quantidade || 1 }];
      else { novo = [...prev]; novo[idx].quantidade += item.quantidade || 1; }
      localStorage.setItem("carrinho", JSON.stringify(novo));
      return novo;
    });
    // Fora do atualizador de propósito: a função passada ao `setCarrinho` pode
    // ser executada duas vezes pelo React, e efeito colateral lá dentro rodaria
    // duas vezes junto. Aqui roda uma.
    descartarChave();
  };

  const removerSalvo = (id, formato) => {
    persistSalvos(salvos.filter((s) => !(s.id === id && s.formato === formato)));
  };

  const adicionarItem = (itemBruto) => {
    // A ÚNICA porta de entrada do carrinho: o saneamento tem de acontecer
    // aqui, antes de qualquer comparação, senão um item sujo criaria uma
    // linha separada da linha limpa do mesmo livro.
    const item = sanearItem(itemBruto);
    const novoCarrinho = [...carrinho];

    // Ajuste: comparar também o formato do item
    const itemIndex = novoCarrinho.findIndex(
      (i) => i.id === item.id && i.formato === item.formato
    );

    if (itemIndex === -1) {
      // Item não existe no carrinho, adicionar com a quantidade especificada
      novoCarrinho.push({ ...item, quantidade: item.quantidade || 1 });
    } else {
      // Item já existe, somar as quantidades
      novoCarrinho[itemIndex].quantidade += (item.quantidade || 1);
    }

    persistCarrinho(novoCarrinho);
  };

  const itemJaNoCarrinho = (id, formato) => {
    // se precisar checar de modo mais completo, inclua também o formato
    return carrinho.some((item) => item.id === id && item.formato === formato);
  };

  const modificarQuantidade = (id, formato, novaQuantidade) => {
    const novoCarrinho = [...carrinho];
    const itemIndex = novoCarrinho.findIndex(
      (i) => i.id === id && i.formato === formato
    );

    if (itemIndex !== -1) {
      if (novaQuantidade <= 0) {
        // Se a quantidade for 0 ou menor, remover o item
        novoCarrinho.splice(itemIndex, 1);
      } else {
        novoCarrinho[itemIndex].quantidade = novaQuantidade;
      }
      persistCarrinho(novoCarrinho);
    }
  };

  const removerItem = (id, formato) => {
    const novoCarrinho = carrinho.filter(
      (item) => !(item.id === id && item.formato === formato)
    );
    persistCarrinho(novoCarrinho);
  };

  const limparCarrinho = () => {
    setCarrinho([]);
    localStorage.removeItem("carrinho");
    descartarChave();
  };

  const totalItens = carrinho.reduce((acc, item) => acc + item.quantidade, 0);

  const valorTotal = carrinho.reduce(
    (acc, item) => acc + item.preco * item.quantidade,
    0
  );

  // Função para calcular o total de livros físicos
  const totalLivrosFisicos = carrinho
    .filter(item => item.formato === 'fisico')
    .reduce((acc, item) => acc + item.quantidade, 0);

  // Função para calcular o multiplicador do frete
  const calcularMultiplicadorFrete = () => {
    return Math.ceil(totalLivrosFisicos / 10);
  };

  // Função para calcular o valor do frete
  const calcularFrete = (freteBase = 20.00) => {
    const multiplicador = calcularMultiplicadorFrete();
    return multiplicador * freteBase;
  };

  // Função para obter informações detalhadas do frete
  const getFreteInfo = () => {
    const livrosFisicos = carrinho.filter(item => item.formato === 'fisico');
    
    if (livrosFisicos.length === 0) {
      return {
        temLivrosFisicos: false,
        totalLivrosFisicos: 0,
        freteTotal: 0,
        multiplicador: 0,
        freteBase: 0,
        explicacao: "Sem frete (apenas e-books)"
      };
    }

    // Buscar informação de frete de qualquer item para obter a modalidade
    let freteBase = 20; // Padrão
    let modalidade = "Padrão (20 dias)";
    let chave = "padrao";
    let cep = "Não informado";
    
    const itemComFrete = livrosFisicos.find(item => item.frete);
    if (itemComFrete && itemComFrete.frete) {
      freteBase = itemComFrete.frete.valor;
      modalidade = itemComFrete.frete.modalidade || (freteBase === 70 ? "Expresso (12 dias)" : "Padrão (20 dias)");
      // A CHAVE que o checkout manda ao servidor. Carrinhos salvos antes de
      // 11/09/2026 não têm `chave` no frete; aí ela sai do valor, que é o
      // que a página do livro gravava.
      chave = itemComFrete.frete.chave || (freteBase === 70 ? "expresso" : "padrao");
      cep = itemComFrete.frete.cep || "Não informado";
    }

    const multiplicador = calcularMultiplicadorFrete();
    const freteTotal = freteBase * multiplicador;

    return {
      temLivrosFisicos: true,
      totalLivrosFisicos,
      multiplicador,
      freteBase,
      freteTotal,
      freteInfo: {
        modalidade,
        chave,
        cep,
        dias: freteBase === 70 ? 12 : 20
      },
      explicacao: multiplicador > 1 
        ? `Frete calculado para ${multiplicador} pacote(s) de até 10 livros cada`
        : totalLivrosFisicos > 0 
          ? "Frete padrão para até 10 livros"
          : "Sem frete (apenas e-books)"
    };
  };

  return (
    <CarrinhoContext.Provider
      value={{
        carrinho,
        salvos,
        adicionarItem,
        itemJaNoCarrinho,
        modificarQuantidade,
        removerItem,
        limparCarrinho,
        salvarParaDepois,
        moverParaCarrinho,
        removerSalvo,
        listaDesejos,
        estaNaLista,
        adicionarDesejo,
        removerDesejo,
        toggleDesejo,
        totalItens,
        valorTotal,
        totalLivrosFisicos,
        calcularMultiplicadorFrete,
        calcularFrete,
        getFreteInfo,
      }}
    >
      {children}
    </CarrinhoContext.Provider>
  );
};

export { CarrinhoContext, CarrinhoProvider };
